import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import LoadingSpinner from '../../../components/ui/LoadingSpinner';
import { useGetSettingsQuery, useUpdateSettingsMutation } from '../../../store/api/settingsApi';

export default function SettingsPage() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useGetSettingsQuery();
  const [updateSettings, { isLoading: isUpdating }] = useUpdateSettingsMutation();

  const [usdRate, setUsdRate] = useState<string>('');
  const [referralPercent, setReferralPercent] = useState<string>('');
  const [welcomeImageUrl, setWelcomeImageUrl] = useState<string>('');

  const settings = data?.data?.settings;

  // Initialize form when settings are loaded
  useEffect(() => {
    if (settings) {
      setUsdRate(settings.usdRate?.toString() || '');
      setReferralPercent((settings.referralPercent ? settings.referralPercent * 100 : 0).toString());
      setWelcomeImageUrl(settings.welcomeImageUrl || '');
    }
  }, [settings]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const updateData: {
        usdRate?: number;
        referralPercent?: number;
        welcomeImageUrl?: string;
      } = {};

      if (usdRate) {
        const parsed = parseFloat(usdRate);
        if (isNaN(parsed) || parsed <= 0) {
          toast.error(t('admin.settings.error.invalidUsdRate') || 'Invalid USD rate');
          return;
        }
        updateData.usdRate = parsed;
      }

      if (referralPercent) {
        const parsed = parseFloat(referralPercent);
        if (isNaN(parsed) || parsed < 0 || parsed > 100) {
          toast.error(t('admin.settings.error.invalidReferralPercent') || 'Invalid referral percentage (0-100)');
          return;
        }
        updateData.referralPercent = parsed / 100; // Convert to decimal
      }

      if (welcomeImageUrl) {
        updateData.welcomeImageUrl = welcomeImageUrl;
      }

      await updateSettings(updateData).unwrap();

      toast.success(t('admin.settings.updateSuccess') || 'Settings updated successfully');
    } catch (err) {
      const error = err as { data?: { error?: string }; message?: string };
      toast.error(error?.data?.error || error?.message || 'Failed to update settings');
    }
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-danger">
          {t('admin.settings.error.title') || 'Error loading settings'}
        </p>
      </div>
    );
  }

  return (
    <div className="px-2 sm:px-4 lg:px-0">
      {/* Header with animation */}
      <div
        className="mb-6 sm:mb-8"
        style={{
          animation: 'fadeIn 0.5s ease-out',
        }}
      >
        <h1 className="text-2xl sm:text-3xl font-heading font-bold text-brand-accent mb-2">
          {t('admin.settings.title') || 'Settings'}
        </h1>
        <p className="text-sm sm:text-base text-neutral-medium">
          {t('admin.settings.subtitle') || 'Manage system settings'}
        </p>
      </div>

      {/* Settings Form */}
      <div
        className="bg-neutral-dark/50 backdrop-blur-lg rounded-lg p-4 sm:p-6 border border-neutral-dark/50"
        style={{
          animation: 'slideUp 0.5s ease-out 0.1s both',
        }}
      >
        <form onSubmit={(e) => { void handleSubmit(e); }} className="space-y-6">
          {/* USD Rate */}
          <div
            style={{
              animation: 'fadeIn 0.5s ease-out 0.2s both',
            }}
          >
            <label htmlFor="usdRate" className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.settings.usdRate') || 'USD Rate (SYP)'}
            </label>
            <input
              id="usdRate"
              type="number"
              step="0.01"
              value={usdRate}
              onChange={(e) => setUsdRate(e.target.value)}
              placeholder={t('admin.settings.usdRatePlaceholder') || 'Enter USD to SYP exchange rate'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
              disabled={isUpdating}
            />
            <p className="text-xs text-neutral-medium mt-1">
              {t('admin.settings.usdRateHint') || 'Current rate: 1 USD = '}
              {settings?.usdRate ? `${settings.usdRate.toLocaleString()} SYP` : 'N/A'}
            </p>
          </div>

          {/* Referral Percentage */}
          <div
            style={{
              animation: 'fadeIn 0.5s ease-out 0.3s both',
            }}
          >
            <label htmlFor="referralPercent" className="block text-sm font-medium text-neutral-light mb-2">
              {t('admin.settings.referralPercent') || 'Referral Percentage (%)'}
            </label>
            <input
              id="referralPercent"
              type="number"
              step="0.01"
              min="0"
              max="100"
              value={referralPercent}
              onChange={(e) => setReferralPercent(e.target.value)}
              placeholder={t('admin.settings.referralPercentPlaceholder') || 'Enter referral percentage (0-100)'}
              className="w-full px-4 py-2 bg-neutral-dark border border-neutral-medium rounded-lg text-neutral-light placeholder-neutral-medium focus:outline-none focus:ring-2 focus:ring-brand-accent focus:border-transparent transition-all duration-200"
              disabled={isUpdating}
            />
            <p className="text-xs text-neutral-medium mt-1">
              {t('admin.settings.referralPercentHint') || 'Current percentage: '}
              {settings?.referralPercent ? `${(settings.referralPercent * 100).toFixed(2)}%` : 'N/A'}
            </p>
          </div>

          {/* Welcome Image URL
          <div
            style={{
              animation: 'fadeIn 0.5s ease-out 0.4s both',
            }}
          >
            {settings?.welcomeImageUrl && (
              <div className="mt-3">
                <p className="text-xs text-neutral-medium mb-2">
                  {t('admin.settings.currentImage') || 'Current Image:'}
                </p>
                <img
                  src={settings.welcomeImageUrl}
                  alt="Welcome"
                  className="max-w-full h-auto rounded-lg border border-neutral-medium"
                  onError={(e) => {
                    (e.target as HTMLImageElement).style.display = 'none';
                  }}
                />
              </div>
            )}
          </div> */}

          {/* Submit Button */}
          <div
            className="flex flex-col sm:flex-row gap-3 pt-4"
            style={{
              animation: 'fadeIn 0.5s ease-out 0.5s both',
            }}
          >
            <button
              type="submit"
              disabled={isUpdating}
              className="w-full sm:w-auto px-6 py-2 bg-brand-accent text-brand-primary font-heading font-semibold rounded-lg hover:bg-brand-accent/90 transition-all duration-200 hover:scale-105 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
            >
              {isUpdating
                ? t('common.loading') || 'Loading...'
                : t('admin.settings.update') || 'Update Settings'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
